import React from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const services = [
  {
    title: "EPC Contracts – Substations up to 220 kV",
    text: `We deliver complete turnkey EPC solutions for substations up to 220 kV, covering engineering, procurement, construction, testing, and commissioning.

Our scope includes detailed engineering, civil and structural works, supply and erection of electrical equipment, protection and control systems, SCADA integration, statutory approvals, and final handover.

Our EPC delivery model ensures single-point accountability, optimized execution schedules, and strict compliance with utility standards and safety regulations.`,
    image: "/services/Substation.jpg",
    imgLeft: true,
  },
  {
    title: "GIS Substation Erection, Testing & Commissioning – up to 400 kV",
    text: `We provide specialized erection, testing, and commissioning services for GIS substations up to 400 kV, executed by trained engineers experienced with leading OEM platforms.

Our capabilities include GIS equipment erection and alignment, SF₆ gas handling, primary and secondary testing, protection relay validation, and energization support.

All activities follow OEM-recommended procedures and international best practices to ensure operational reliability.`,
    image: "/services/Gis-substation.jpg",
    imgLeft: false,
  },
  {
    title: "Cable Laying & Termination Services – up to 220 kV",
    text: `We offer comprehensive HT and LT cable laying, jointing, and termination services for substations and transmission projects.

Our expertise covers trenching and ducting, HT cable laying up to 220 kV, indoor and outdoor terminations, straight-through joints, testing, and pre-commissioning documentation.

We ensure precision workmanship, adherence to standards, and minimal disruption to ongoing operations.`,
    image: "/services/cables.jpg",
    imgLeft: true,
  },
  {
    title: "Substation Automation & Digital Solutions",
    text: `We deliver IEC 61850–compliant Substation Automation Systems (SAS) to support smart and future-ready grid infrastructure.

Our solutions include SAS engineering and commissioning, RTU supply and integration, SCADA connectivity, control room systems, and modernization of existing substations.

These systems enhance operational visibility, asset intelligence, and system security.`,
    image: "/services/automation.jpg",
    imgLeft: false,
  },
  {
    title: "Control, Relay & Panel Solutions",
    text: `We design, manufacture, and supply utility-grade electrical panels engineered to meet technical specifications and long-term operational reliability.

Our product portfolio includes Control & Relay Panels (CRP), protection and metering panels, RTU panels, LT panels, and PLC-based automation panels.

All panels are factory-tested and delivered with complete documentation and quality assurance.`,
    image: "/services/control-relay-panel.jpg",
    imgLeft: true,
  },
  {
    title: "Equipment Supply",
    text: `We supply utility-approved electrical equipment sourced from reputed OEM partners, ensuring quality, compliance, and timely delivery.

Our portfolio includes capacitor banks, HT switchgear, LT switchboards, HT & LT power cables, and substation auxiliaries.

As authorized partners for Havells and PROK DVS, we deliver certified products backed by OEM technical support.`,
    image: "/services/product-last.jpg",
    imgLeft: false,
  },
];

const Services = () => {
  return (
    <Box sx={{ backgroundColor: "#f4f6f8" }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        sx={{
          position: "relative",
          height: { xs: "38vh", md: "46vh" },
          backgroundImage: "url('/services/Services-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.6))",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: "#ffffff",
              letterSpacing: 1,
              mb: 1,
            }}
          >
            Our Services
          </Typography>

          <Box
            sx={{
              width: 90,
              height: 4,
              backgroundColor: "#ffffff",
              borderRadius: 2,
              mx: "auto",
              mt: 3,
            }}
          />
        </Box>
      </Box>

      {/* Service Sections */}
      {services.map((service, idx) => (
        <Box
          key={idx}
          sx={{
            py: { xs: 8, md: 11 },
            backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f0f4f8",
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={6}
              alignItems="center"
              direction={service.imgLeft ? "row" : "row-reverse"}
            >
              {/* Image */}
              <Grid item xs={12} md={5}>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <Box
                    component="img"
                    src={service.image}
                    alt={service.title}
                    sx={{
                      width: "100%",
                      maxWidth: 420,
                      borderRadius: 4,
                      boxShadow: "0 14px 35px rgba(0,0,0,0.15)",
                      mx: "auto",
                    }}
                  />
                </motion.div>
              </Grid>

              {/* Content */}
              <Grid item xs={12} md={7}>
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <Box sx={{ maxWidth: 660, mx: "auto" }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#1e3a8a",
                        mb: 3,
                      }}
                    >
                      {service.title}
                    </Typography>

                    {service.text.split("\n\n").map((para, i) => (
                      <Typography
                        key={i}
                        sx={{
                          fontSize: "1.05rem",
                          lineHeight: 1.85,
                          color: "#374151",
                          textAlign: "justify",
                          mb: 2.2,
                        }}
                      >
                        {para}
                      </Typography>
                    ))}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ))}
    </Box>
  );
};

export default Services;